// frontend/source/portal/components/EditArtistCard.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ImagePicker from "./ImagePicker";
import "./EditArtistCard.css";

const EditArtistCard = ({ artist, onDelete, onUpdate }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [fname, setFname] = useState(artist.fname);
  const [lname, setLname] = useState(artist.lname);
  const [gender, setGender] = useState(artist.gender || "");
  const [birthdate, setBirthdate] = useState(artist.birthdate || "");
  const [isSaving, setIsSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);

  const getDisplayName = () => {
    return artist.fullName || `${artist.fname} ${artist.lname}`;
  };

  const handleSave = async () => {
    if (fname.trim() === "") {
      alert("First name cannot be empty");
      return;
    }

    if (lname.trim() === "") {
      alert("Last name cannot be empty");
      return;
    }

    // Check if anything changed
    if (
      fname === artist.fname &&
      lname === artist.lname &&
      gender === (artist.gender || "") &&
      birthdate === (artist.birthdate || "")
    ) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      const updateData = {
        fname,
        lname,
        gender: gender || undefined,
        birthdate: birthdate || undefined,
      };
      await onUpdate(artist._id, updateData);
      setIsEditing(false);
    } catch (err) {
      setFname(artist.fname);
      setLname(artist.lname);
      setGender(artist.gender || "");
      setBirthdate(artist.birthdate || "");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFname(artist.fname);
    setLname(artist.lname);
    setGender(artist.gender || "");
    setBirthdate(artist.birthdate || "");
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleImageSelect = async (imageUrl) => {
    try {
      setIsSaving(true);
      await onUpdate(artist._id, { imageUrl });
    } catch (err) {
      console.error("Failed to update image:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleManageArtworks = () => {
    navigate(`/portal/student/${artist._id}/artworks`);
  };

  const artistImage = artist.imageUrl || "/images/default-artist.jpg";

  return (
    <div className="edit-artist-card">
      <div className="edit-artist-card-image-container">
        <img
          src={artistImage}
          alt={getDisplayName()}
          className="edit-artist-card-image"
          onClick={handleManageArtworks}
        />
        <button
          onClick={() => setShowImagePicker(true)}
          className="edit-artist-change-image-btn"
          title="Change image"
        >
          📷
        </button>
      </div>
      <div className="edit-artist-card-overlay">
        {isEditing ? (
          <div className="edit-artist-input-container">
            <div className="edit-artist-name-row">
              <input
                type="text"
                value={fname}
                onChange={(e) => setFname(e.target.value)}
                onKeyDown={handleKeyPress}
                className="edit-artist-input"
                placeholder="First name"
                autoFocus
                disabled={isSaving}
              />
              <input
                type="text"
                value={lname}
                onChange={(e) => setLname(e.target.value)}
                onKeyDown={handleKeyPress}
                className="edit-artist-input"
                placeholder="Last name"
                disabled={isSaving}
              />
            </div>

            <div className="edit-artist-details-row">
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="edit-artist-select"
                disabled={isSaving}
              >
                <option value="">Gender (optional)</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              <input
                type="date"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                className="edit-artist-input"
                disabled={isSaving}
              />
            </div>

            <div className="edit-artist-buttons">
              <button
                onClick={handleSave}
                className="edit-artist-btn save-btn"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancel}
                className="edit-artist-btn cancel-btn"
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="edit-artist-card-title">{getDisplayName()}</h2>
            <div className="edit-artist-actions">
              <button
                onClick={() => setIsEditing(true)}
                className="edit-artist-action-btn edit-btn"
                title="Edit artist"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(artist._id)}
                className="edit-artist-action-btn delete-btn"
                title="Delete artist"
              >
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      {showImagePicker && (
        <ImagePicker
          currentImage={artistImage}
          onImageSelect={handleImageSelect}
          onClose={() => setShowImagePicker(false)}
        />
      )}
    </div>
  );
};

export default EditArtistCard;
